import * as XLSX from 'xlsx';

export const UNIVERSITY_ID_STATUS_OPTIONS = [
  { value: 'assigned', label: 'Has University ID' },
  { value: 'missing', label: 'Missing University ID' },
];

// Null-safe status key for a student's University ID assignment.
export function normalizedUniversityIdStatus(universityID) {
  return universityID ? 'assigned' : 'missing';
}

export function universityIdLabel(universityID) {
  return universityID || 'Not Assigned';
}

export function universityIdBadgeClass(universityID) {
  return universityID ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700';
}

export function universityIdBadgeStyle(universityID) {
  return universityID
    ? { background: '#d1fae5', color: '#047857' }
    : { background: '#fee2e2', color: '#b91c1c' };
}

// Whole months elapsed between a batch's start date and today — mirrors the
// TIMESTAMPDIFF(MONTH, ...) check the backend already uses elsewhere for
// flagging overdue university registrations. Returns null when there's no
// (parseable) start date.
export function monthsSinceBatchStart(batchStartingDate) {
  if (!batchStartingDate) return null;
  const start = new Date(batchStartingDate);
  if (Number.isNaN(start.getTime())) return null;
  const now = new Date();
  let months = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
  if (now.getDate() < start.getDate()) months -= 1;
  return Math.max(0, months);
}

// Per-status headcount for a batch's student list — keys are
// UNIVERSITY_ID_STATUS_OPTIONS values ('assigned' / 'missing').
export function computeBatchUniversityIdCounts(students) {
  const counts = { assigned: 0, missing: 0 };
  (students || []).forEach((s) => {
    const key = normalizedUniversityIdStatus(s.universityID);
    counts[key] = (counts[key] || 0) + 1;
  });
  return counts;
}

// Keeps only students whose University ID status is in `statusValues`
// ('assigned' / 'missing'), dropping batches/courses left with no students.
// An empty/undefined `statusValues` means "no filter".
export function filterCoursesByUniversityIdStatus(courses, statusValues) {
  if (!Array.isArray(statusValues) || statusValues.length === 0) return courses;
  const wanted = new Set(statusValues.map((s) => String(s).toLowerCase()));
  return courses
    .map((course) => ({
      ...course,
      batches: course.batches
        .map((batch) => ({
          ...batch,
          students: batch.students.filter((s) => wanted.has(normalizedUniversityIdStatus(s.universityID))),
        }))
        .filter((batch) => batch.students.length > 0),
    }))
    .filter((course) => course.batches.length > 0);
}

function toDdMmYyyy(isoDate) {
  if (!isoDate) return '';
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
}

// Flattens { student, batch } pairs into the row shape the export template
// needs — one row per student, carrying along the batch it was pulled from.
export function buildUniversityIdExportRows(entries) {
  return entries.map(({ student, batch }) => ({
    studentId: student.studentId || '',
    studentName: student.studentName || '',
    registrationNumber: student.registrationNumber || '',
    batchName: batch.batchInternalName || batch.whatsappGroupName || batch.batchName || '',
    whatsappGroupName: batch.whatsappGroupName || '',
    universityID: student.universityID || '',
    universityRegisterDate: student.universityRegisterDate || '',
  }));
}

function buildInstructionsSheet() {
  const rows = [
    ['University ID Status — Bulk Update Template'],
    [],
    ['How to use this sheet'],
    ['1. The "University ID" sheet lists students from the batch/course/status you clicked Download on.'],
    ['2. "Current University ID" and "Current Registered On" show today\'s value for reference only — they are ignored on re-upload.'],
    ['3. Fill in "University ID" and/or "University Register Date (dd/mm/yyyy)" only for students you want to update. Leave both cells blank to leave that student unchanged.'],
    ['4. Dates must be typed as dd/mm/yyyy (e.g. 31/01/2026).'],
    ['5. When done, go to Admin > Students > Upload University Registration Template (/admin/students/university-registration-upload) and upload this file.'],
    ['6. Rows are matched by Student ID + Registration Number, so do not edit those two columns.'],
  ];
  const sheet = XLSX.utils.aoa_to_sheet(rows);
  sheet['!cols'] = [{ wch: 100 }];
  return sheet;
}

// Builds and downloads an .xlsx workbook in the exact column layout the
// existing /admin/students/university-registration-upload page expects
// (required headers: "Student ID", "Registration Number", "University ID",
// "University Register Date (dd/mm/yyyy)"), so a cell's exported sheet can
// be edited and re-uploaded there to bulk-update registrations.universityID
// / registrations.universityRegisterDate. Returns false (and downloads
// nothing) when there are no rows to export.
export function downloadUniversityIdTemplate(rows, filenameSuffix) {
  if (!Array.isArray(rows) || rows.length === 0) return false;

  const headers = [
    'Student ID',
    'Student Name',
    'Registration Number',
    'Batch Name',
    'Batch WhatsApp Group',
    'Current University ID',
    'University ID',
    'Current Registered On',
    'University Register Date (dd/mm/yyyy)',
  ];

  const dataRows = rows.map((r) => [
    r.studentId,
    r.studentName,
    r.registrationNumber,
    r.batchName,
    r.whatsappGroupName,
    // "Current …" columns are reference-only (ignored on re-upload), so they
    // always show something readable — never a blank cell that looks broken.
    r.universityID || 'Not Assigned',
    r.universityID,
    r.universityRegisterDate ? toDdMmYyyy(r.universityRegisterDate) : 'Not set',
    toDdMmYyyy(r.universityRegisterDate),
  ]);

  const worksheet = XLSX.utils.aoa_to_sheet([headers, ...dataRows]);
  const workbook = XLSX.utils.book_new();
  // "University ID" MUST be the first sheet — the existing
  // /admin/students/university-registration-upload page only ever reads
  // workbook.SheetNames[0] as the data sheet.
  XLSX.utils.book_append_sheet(workbook, worksheet, 'University ID');
  XLSX.utils.book_append_sheet(workbook, buildInstructionsSheet(), 'Instructions');

  const timestamp = new Date().toISOString().split('T')[0];
  const safeSuffix = String(filenameSuffix || 'Export').replace(/[^a-z0-9]+/gi, '_');
  XLSX.writeFile(workbook, `University_ID_Status_${safeSuffix}_${timestamp}.xlsx`);
  return true;
}
